import { defineCard, } from '@cards/engine';
import { assertDeckPassiveId } from '../deck-passive-id.js';
import { isPixelGlyph } from './pixel-glyph.js';
const TOP_LEVEL_KEYS = new Set(['id', 'presentation', 'role', 'initialEffect', 'hooks']);
const PRESENTATION_KEYS = new Set(['name', 'description', 'glyph']);
const INITIAL_EFFECT_KEYS = new Set(['vars', 'statMods']);
function assertOnlyKeys(value, allowed, owner) {
    for (const key of Reflect.ownKeys(value)) {
        if (!allowed.has(key))
            throw new Error(`${owner}: неизвестное поле "${String(key)}"`);
    }
}
function assertRecord(value, owner) {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        throw new Error(`${owner} должен быть объектом`);
    }
    const prototype = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) {
        throw new Error(`${owner} должен быть обычным JSON-объектом`);
    }
}
function assertJsonValue(value, owner, ancestors = new Set()) {
    if (value === null || typeof value === 'string' || typeof value === 'boolean')
        return;
    if (typeof value === 'number') {
        if (!Number.isFinite(value))
            throw new Error(`${owner} содержит не-конечное JSON-число`);
        return;
    }
    if (typeof value !== 'object')
        throw new Error(`${owner} содержит несериализуемое значение`);
    if (ancestors.has(value))
        throw new Error(`${owner} содержит циклическую ссылку`);
    ancestors.add(value);
    if (Array.isArray(value)) {
        if (Object.getPrototypeOf(value) !== Array.prototype) {
            throw new Error(`${owner} должен быть обычным JSON-массивом`);
        }
        const allowedKeys = new Set(['length', ...value.map((_entry, index) => String(index))]);
        for (const key of Reflect.ownKeys(value)) {
            if (typeof key !== 'string' || !allowedKeys.has(key)) {
                throw new Error(`${owner} содержит нестандартное поле массива`);
            }
        }
        for (let index = 0; index < value.length; index += 1) {
            const descriptor = Object.getOwnPropertyDescriptor(value, String(index));
            if (descriptor?.enumerable !== true || !('value' in descriptor)) {
                throw new Error(`${owner}[${index}] должен быть обычным JSON-элементом`);
            }
            assertJsonValue(descriptor.value, `${owner}[${index}]`, ancestors);
        }
    }
    else {
        assertRecord(value, owner);
        for (const key of Reflect.ownKeys(value)) {
            if (typeof key !== 'string')
                throw new Error(`${owner} содержит symbol key`);
            const descriptor = Object.getOwnPropertyDescriptor(value, key);
            if (descriptor?.enumerable !== true || !('value' in descriptor)) {
                throw new Error(`${owner}.${key} должен быть обычным enumerable JSON-полем`);
            }
            assertJsonValue(descriptor.value, `${owner}.${key}`, ancestors);
        }
    }
    ancestors.delete(value);
}
function deepFreezeJson(value) {
    if (typeof value !== 'object' || value === null || Object.isFrozen(value))
        return value;
    if (Array.isArray(value))
        value.forEach(deepFreezeJson);
    else
        Object.values(value).forEach(deepFreezeJson);
    return Object.freeze(value);
}
function normalizeGlyph(glyph, owner) {
    assertRecord(glyph, `${owner}.glyph`);
    assertOnlyKeys(glyph, new Set(['colors', 'rows']), `${owner}.glyph`);
    if (!isPixelGlyph(glyph)) {
        throw new Error(`${owner}: некорректный glyph`);
    }
    const { colors, rows } = glyph;
    return Object.freeze({
        colors: Object.freeze([...colors]),
        rows: Object.freeze([...rows]),
    });
}
function normalizeStatMods(value, owner) {
    if (value === undefined)
        return undefined;
    if (!Array.isArray(value))
        throw new Error(`${owner}.statMods должен быть массивом`);
    if (Object.getPrototypeOf(value) !== Array.prototype) {
        throw new Error(`${owner}.statMods должен быть обычным массивом`);
    }
    const allowedKeys = new Set([
        'length',
        ...Array.from({ length: value.length }, (_entry, index) => String(index)),
    ]);
    for (const key of Reflect.ownKeys(value)) {
        if (typeof key !== 'string' || !allowedKeys.has(key)) {
            throw new Error(`${owner}.statMods содержит нестандартное поле массива`);
        }
    }
    for (let index = 0; index < value.length; index += 1) {
        const descriptor = Object.getOwnPropertyDescriptor(value, String(index));
        if (descriptor?.enumerable !== true || !('value' in descriptor)) {
            throw new Error(`${owner}.statMods[${index}] должен быть обычным элементом массива`);
        }
    }
    return Object.freeze(value.map((candidate, index) => {
        const where = `${owner}.statMods[${index}]`;
        assertRecord(candidate, where);
        const keys = Object.keys(candidate);
        if (keys.length !== 3 || keys.some((key) => !['stat', 'op', 'value'].includes(key))) {
            throw new Error(`${where} должен содержать только stat/op/value`);
        }
        if (candidate.stat !== 'attack' && candidate.stat !== 'maxHealth') {
            throw new Error(`${where}.stat должен быть attack или maxHealth`);
        }
        if (candidate.op !== 'add' && candidate.op !== 'mul' && candidate.op !== 'set') {
            throw new Error(`${where}.op должен быть add, mul или set`);
        }
        if (typeof candidate.value !== 'number' || !Number.isFinite(candidate.value)) {
            throw new Error(`${where}.value должен быть конечным числом`);
        }
        return Object.freeze({
            stat: candidate.stat,
            op: candidate.op,
            value: Object.is(candidate.value, -0) ? 0 : candidate.value,
        });
    }));
}
export function defineDeckPassive(input) {
    assertRecord(input, 'defineDeckPassive input');
    const owner = `defineDeckPassive "${String(input.id)}"`;
    assertOnlyKeys(input, TOP_LEVEL_KEYS, owner);
    assertDeckPassiveId(input.id, 'defineDeckPassive: id');
    if (input.role !== 'available' && input.role !== 'legacy') {
        throw new Error(`${owner}: неизвестная role "${String(input.role)}"`);
    }
    assertRecord(input.presentation, `${owner}.presentation`);
    assertOnlyKeys(input.presentation, PRESENTATION_KEYS, `${owner}.presentation`);
    if (typeof input.presentation.name !== 'string' || input.presentation.name.length === 0
        || typeof input.presentation.description !== 'string' || input.presentation.description.length === 0) {
        throw new Error(`${owner}: name и description должны быть непустыми`);
    }
    const glyph = normalizeGlyph(input.presentation.glyph, `${owner}.presentation`);
    const initial = input.initialEffect ?? {};
    assertRecord(initial, `${owner}.initialEffect`);
    assertOnlyKeys(initial, INITIAL_EFFECT_KEYS, `${owner}.initialEffect`);
    const varsInput = initial.vars ?? {};
    assertRecord(varsInput, `${owner}.initialEffect.vars`);
    assertJsonValue(varsInput, `${owner}.initialEffect.vars`);
    const vars = deepFreezeJson(structuredClone(varsInput));
    const statMods = normalizeStatMods(initial.statMods, `${owner}.initialEffect`);
    const definition = defineCard({
        id: input.id,
        type: 'EFFECT',
        ...(input.hooks === undefined ? {} : { hooks: input.hooks }),
    });
    return Object.freeze({
        id: input.id,
        definition,
        presentation: Object.freeze({
            id: input.id,
            name: input.presentation.name,
            description: input.presentation.description,
            glyph,
        }),
        role: input.role,
        initialEffect: Object.freeze({
            vars,
            ...(statMods === undefined ? {} : { statMods }),
        }),
        source: 'deck_passive',
    });
}
/** Convert authored content into the engine's product-neutral init contract. */
export function deckPassiveStartingEffect(passive) {
    return Object.freeze({
        defId: passive.id,
        vars: Object.freeze(structuredClone(passive.initialEffect.vars)),
        ...(passive.initialEffect.statMods === undefined
            ? {}
            : { statMods: Object.freeze(structuredClone(passive.initialEffect.statMods)) }),
    });
}
//# sourceMappingURL=define-deck-passive.js.map